<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCurrency } from 'dashboard/composables/useCurrency';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import MetricHelp from '../MetricHelp.vue';

const props = defineProps({
  data: {
    type: Object,
    default: null,
  },
});

const { locale } = useI18n();
const browserLocale = computed(() => locale.value.replace('_', '-'));
const { currency } = useCurrency();

// Estado
const sortColumn = ref('total_value');
const sortDirection = ref('desc');
const expandedFunnel = ref(null);

// Computed
const funnels = computed(() => {
  if (!props.data?.funnels) return [];

  const funnelsList = [...props.data.funnels];

  funnelsList.sort((a, b) => {
    const aValue = a[sortColumn.value];
    const bValue = b[sortColumn.value];

    if (sortColumn.value === 'average_time') {
      const aSeconds = aValue?.seconds || 0;
      const bSeconds = bValue?.seconds || 0;
      return sortDirection.value === 'asc'
        ? aSeconds - bSeconds
        : bSeconds - aSeconds;
    }

    if (sortDirection.value === 'asc') {
      return aValue > bValue ? 1 : -1;
    }
    return aValue < bValue ? 1 : -1;
  });

  return funnelsList;
});

// Métodos
const formatCurrency = value => {
  return new Intl.NumberFormat(browserLocale.value, {
    style: 'currency',
    currency: currency.value,
  }).format(value);
};

const sortBy = column => {
  if (sortColumn.value === column) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortColumn.value = column;
    sortDirection.value = 'desc';
  }
};

const toggleExpand = funnelId => {
  expandedFunnel.value = expandedFunnel.value === funnelId ? null : funnelId;
};

const getSortIcon = column => {
  if (sortColumn.value !== column) return 'i-lucide-arrow-down-up';
  return sortDirection.value === 'asc'
    ? 'i-lucide-arrow-up'
    : 'i-lucide-arrow-down';
};
</script>

<template>
  <div
    class="bg-white dark:bg-n-slate-2 border border-n-slate-6 rounded-lg overflow-hidden"
  >
    <div class="px-4 py-3 border-b border-n-slate-6">
      <h3 class="text-lg font-semibold text-n-slate-12">
        {{ $t('KANBAN_REPORTS.TABLES.FUNNELS_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11 mt-1">
        {{ $t('KANBAN_REPORTS.TABLES.FUNNELS_DESCRIPTION') }}
      </p>
    </div>

    <div class="overflow-x-auto">
      <table class="w-full">
        <thead class="bg-n-slate-1 dark:bg-n-slate-3">
          <tr>
            <th class="px-4 py-3 w-10" />
            <th
              class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider cursor-pointer hover:bg-n-slate-2 dark:hover:bg-n-slate-4 transition-colors"
              @click="sortBy('name')"
            >
              <div class="flex items-center gap-2">
                {{ $t('KANBAN_REPORTS.TABLES.FUNNEL') }}
                <Icon :icon="getSortIcon('name')" class="size-3.5" />
              </div>
            </th>
            <th
              class="px-4 py-3 text-right text-xs font-medium text-n-slate-11 uppercase tracking-wider cursor-pointer hover:bg-n-slate-2 dark:hover:bg-n-slate-4 transition-colors"
              @click="sortBy('total_cards')"
            >
              <div class="flex items-center justify-end gap-2">
                {{ $t('KANBAN_REPORTS.TABLES.TOTAL_CARDS') }}
                <Icon :icon="getSortIcon('total_cards')" class="size-3.5" />
              </div>
            </th>
            <th
              class="px-4 py-3 text-right text-xs font-medium text-n-slate-11 uppercase tracking-wider cursor-pointer hover:bg-n-slate-2 dark:hover:bg-n-slate-4 transition-colors"
              @click="sortBy('total_value')"
            >
              <div class="flex items-center justify-end gap-2">
                {{ $t('KANBAN_REPORTS.TABLES.TOTAL_VALUE') }}
                <Icon :icon="getSortIcon('total_value')" class="size-3.5" />
              </div>
            </th>
            <th
              class="px-4 py-3 text-right text-xs font-medium text-n-slate-11 uppercase tracking-wider cursor-pointer hover:bg-n-slate-2 dark:hover:bg-n-slate-4 transition-colors"
              @click="sortBy('conversion_rate')"
            >
              <div class="flex items-center justify-end gap-2">
                {{ $t('CRM_METRICS.WIN_RATE.NAME') }}
                <MetricHelp metric-key="WIN_RATE" />
                <Icon :icon="getSortIcon('conversion_rate')" class="size-3.5" />
              </div>
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-slate-6">
          <template v-for="funnel in funnels" :key="funnel.id">
            <tr
              class="hover:bg-n-slate-1 dark:hover:bg-n-slate-3 transition-colors"
            >
              <td class="px-4 py-3">
                <button
                  class="p-1 hover:bg-n-slate-2 rounded transition-colors"
                  @click="toggleExpand(funnel.id)"
                >
                  <Icon
                    :icon="
                      expandedFunnel === funnel.id
                        ? 'i-lucide-chevron-down'
                        : 'i-lucide-chevron-right'
                    "
                    class="size-4 text-n-slate-11"
                  />
                </button>
              </td>
              <td class="px-4 py-3">
                <div class="flex items-center gap-2">
                  <div
                    class="w-3 h-3 rounded"
                    :style="{ backgroundColor: funnel.color || '#1F93FF' }"
                  />
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ funnel.name }}
                  </span>
                </div>
              </td>
              <td class="px-4 py-3 text-right text-sm text-n-slate-12">
                {{ funnel.total_cards }}
              </td>
              <td
                class="px-4 py-3 text-right text-sm font-medium text-green-600 dark:text-green-400"
              >
                {{ formatCurrency(funnel.total_value) }}
              </td>
              <td
                class="px-4 py-3 text-right text-sm font-medium text-blue-600 dark:text-blue-400"
              >
                {{ funnel.conversion_rate }}%
              </td>
            </tr>

            <!-- Stages expandidos -->
            <tr
              v-if="expandedFunnel === funnel.id"
              class="bg-n-slate-1 dark:bg-n-slate-3"
            >
              <td colspan="5" class="px-4 py-3">
                <div class="pl-8">
                  <h4 class="text-xs font-semibold text-n-slate-11 mb-2">
                    {{ $t('KANBAN_REPORTS.TABLES.STAGES') }}
                  </h4>
                  <div class="space-y-2">
                    <div
                      v-for="stage in funnel.stages"
                      :key="stage.name"
                      class="flex items-center justify-between p-2 bg-white dark:bg-n-slate-2 rounded"
                    >
                      <div class="flex items-center gap-2">
                        <div
                          class="w-2 h-2 rounded"
                          :style="{ backgroundColor: stage.color }"
                        />
                        <span class="text-xs font-medium text-n-slate-12">
                          {{ stage.name }}
                        </span>
                      </div>
                      <div
                        class="flex items-center gap-4 text-xs text-n-slate-11"
                      >
                        <span>
                          {{ stage.card_count }}
                          {{ $t('KANBAN_REPORTS.CARDS') }}
                        </span>
                        <span
                          class="font-medium text-green-600 dark:text-green-400"
                        >
                          {{ formatCurrency(stage.total_value) }}
                        </span>
                        <span>{{ stage.average_time?.formatted }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </template>

          <tr v-if="funnels.length === 0">
            <td
              colspan="5"
              class="px-4 py-8 text-center text-sm text-n-slate-11"
            >
              {{ $t('KANBAN_REPORTS.TABLES.NO_DATA') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
